import React, { useEffect, useState } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import produce from "immer";
import _ from "lodash";
import Button from "../Shared/Button";
import CheckboxField from "../Shared/CheckboxField";
import { ADMIN_ROLE, TIME_FORMAT, WORKFLOW_APPT_STATUS_OPTIONS } from "../../constants";
import moment from "moment";
import "./styles/index.css";
import { getUsers } from "../../services/auth";
import { isGPReportListEnabled } from "../../infra/featureToggle";
import { Skeleton } from "antd";

const animatedComponents = makeAnimated();
const taskList = [
  { id: "appointment_t", name: "Bookings" },
  { id: "pre_consult_t", name: "Results" },
  { id: "gp_report_t", name: "GP Report Status" },
];

const statuses = [
  { label: "Not Started", value: "NS" },
  { label: "Pending", value: "PD" },
  { label: "Completed", value: "CP" },
];

const gp_statuses = [
  { label: "Not Completed", value: "NC" },
  { label: "Query", value: "QR" },
  { label: "Completed", value: "CP" },
];

const inspectionOptions = [
  { value: "Pathology", label: "Pathology" },
  { value: "MRI", label: "MRI" },
  { value: "CT", label: "CT" },
  { value: "X-Ray", label: "X-Ray" },
  { value: "Sleep Study", label: "Sleep Study" },
  { value: "DEXA Scan", label: "DEXA Scan" },
  { value: "CTCA", label: "CTCA" },
  { value: "Nerve Conduction Study", label: "Nerve Conduction Study" },
  { value: "Lung Function Test", label: "Lung Function Test" },
  { value: "EEG", label: "EEG" },
  { value: "ECG", label: "ECG" },
  { value: "Echocardiogram", label: "Echocardiogram" },
  { value: "Ultrasound", label: "Ultrasound" },
  { value: "New Referral", label: "New Referral" },
  { value: "Broncoscopy", label: "Broncoscopy" },
  { value: "Thryroid Scan", label: "Thryroid Scan" },
  { value: "Radiology", label: "Radiology" },
  { value: "Clinical Letter", label: "Clinical Letter" },
  { value: "Previous Records", label: "Previous Records" },
  { value: "Payment", label: "Payment" },
  { value: "BMD", label: "BMD" },
  { value: "Breath Test", label: "Breath Test" },
  { value: "Histology", label: "Histology" },
  { value: "Diabetes Sugar Record", label: "Diabetes Sugar Record" },
  { value: "Pump Download", label: "Pump Download" },
  { value: 'PRF  +/- "CC on file"', label: 'PRF  +/- "CC on file"' },
  { value: "Optometry Report", label: "Optometry Report" },
  { value: "Blood Pressure", label: "Blood Pressure" },
  { value: "Vanderbilt", label: "Vanderbilt" },
  { value: "Urine Pathology", label: "Urine Pathology" },
  { value: "Holter Monitor", label: "Holter Monitor" },
  { value: "Thyroid Results", label: "Thyroid Results" },
  { value: "Stool Test", label: "Stool Test" },
  { value: "ADHD GP Consent", label: "ADHD GP Consent" },
];

const statusesMapping = {
  appointment_t: WORKFLOW_APPT_STATUS_OPTIONS,
  gp_report_t: gp_statuses,
  pre_consult_t: statuses,
  letter_t: statuses,
};

const autoNotesSeparator = "\n----------------\n[AUTO NOTES]\n";

const CoordinatorSelect = ({ isLoading, coordinators, value, onChange }) => {
  if (isLoading) {
    return (
      <div className="coordinator-skeleton">
        <Skeleton.Input active block size="small" />
      </div>
    );
  }
  return (
    <div className="select is-size-7 is-fullwidth">
      <select className="is-radiusless" onChange={onChange} value={value}>
        <option value="">Coordinator</option>
        {coordinators.map((person, idx) => (
          <option key={idx} value={person.value}>
            {person.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const ManageTasksForm = ({
  caTasks,
  finNo,
  createdDate,
  submit,
  isLoading = false,
  isGpReportSelected = true,
  outboundLetterCount = 0,
}) => {
  // On `isGPReportListEnabled` tenants the GP Report status is meaningful only
  // once a coordinator has picked one of the outbound letters as the actual GP
  // report. Until that happens, we disable the status select so the workflow
  // can't be flipped to "Completed" without a target file (the auto-email
  // would have nothing to attach).
  const mustPickGpReport = isGPReportListEnabled && !isGpReportSelected;
  const gpReportHint = (() => {
    if (!mustPickGpReport) return null;
    if (outboundLetterCount > 0) {
      return `Select a GP report from Outbound Letters (${outboundLetterCount}) before updating this status.`;
    }
    return "Waiting for a GP report from PMS. Status can be updated once a letter is received and selected.";
  })();
  const [tasks, setTasks] = useState({
    appointment_t: {
      coordinator: "",
      status: "NS",
      notes: "",
      urgent: false,
      biologics: false,
      interpreter: false,
      weeks_to_appointment: "0",
      expected_appointment_date: null,
      is_new_appointment: false,
      triage: false,
      cont_or_tbc: "",
    },
    pre_consult_t: { coordinator: "", status: "", notes: "", urgent: false, inspections: [] },
    letter_t: { coordinator: "", status: "", notes: "", urgent: false },
    gp_report_t: { status: "", is_nurse_check: false },
  });
  const [alreadySelectOptions, setAlreadySelectOptions] = useState([]);
  const [bookingDate, setBookingDate] = useState("");
  const [updatedFinNo, setUpdatedFinNo] = useState(null);
  const [coordinators, setCoordinators] = useState([]);
  const [isCoordinatorsLoading, setIsCoordinatorsLoading] = useState(true);

  const transformWeekToDate = (createDateTimestamp, weeks) => {
    const weekCount = _.toNumber(weeks) || 0;
    return moment.unix(createDateTimestamp).startOf("day").add(weekCount, "weeks").format(TIME_FORMAT.DATE_WITH_DASH);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const users = await getUsers(ADMIN_ROLE);
        if (!mounted) return;
        setCoordinators(
          (users?.filter((user) => !user.isITStaff && !user.disabled) || []).map((user) => ({
            label: `${user.firstName} ${user.lastName}`,
            value: user.shortName,
          }))
        );
      } catch (error) {
        console.error("Failed to load coordinators", error);
        setCoordinators([]);
      } finally {
        if (mounted) setIsCoordinatorsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setUpdatedFinNo(finNo);
  }, [finNo]);

  useEffect(() => {
    if (!_.isEmpty(caTasks)) {
      const exactDate = _.get(caTasks, "appointment_t.expected_appointment_date", null);
      const initialWeeks = _.get(caTasks, "appointment_t.weeks_to_appointment", "0");
      setTasks((prev) => ({
        ...prev,
        ...caTasks,
        appointment_t: {
          ...prev.appointment_t,
          ...caTasks.appointment_t,
          expected_appointment_date: exactDate,
        },
      }));
      setBookingDate(exactDate || (createdDate ? transformWeekToDate(createdDate, initialWeeks) : ""));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setAlreadySelectOptions(
      tasks.pre_consult_t.inspections.map((inspection) => {
        return { label: inspection, value: inspection };
      })
    );
    // eslint-disable-next-line
  }, [tasks.pre_consult_t.inspections]);

  const prepareTasks = (tasks) => {
    const preConsultTaskNotes = [
      (tasks["pre_consult_t"].notes || "").split(autoNotesSeparator)[0],
      tasks["pre_consult_t"].inspections,
    ].join(autoNotesSeparator);
    const aptTaskNotes = [
      (tasks.appointment_t.notes || "").split(autoNotesSeparator)[0],
      tasks.appointment_t.cont_or_tbc,
    ].join(autoNotesSeparator);
    const submittingTasks = {
      ...tasks,
      pre_consult_t: {
        ...tasks.pre_consult_t,
        notes: preConsultTaskNotes === autoNotesSeparator ? "" : preConsultTaskNotes,
      },
      appointment_t: {
        ...tasks.appointment_t,
        notes: aptTaskNotes === autoNotesSeparator ? "" : aptTaskNotes,
      },
    };
    return submittingTasks;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submittingTasks = prepareTasks(tasks);
    submit(submittingTasks, caTasks, updatedFinNo);
    setTasks(submittingTasks);
  };

  const handleChangeUrgency = (taskId) => {
    setTasks({
      ...tasks,
      [taskId]: {
        ...tasks[taskId],
        urgent: !tasks[taskId].urgent,
      },
    });
  };
  const handleChangeBiologics = (taskId) => {
    setTasks({
      ...tasks,
      [taskId]: {
        ...tasks[taskId],
        biologics: !tasks[taskId].biologics,
      },
    });
  };
  const handleChangeInterpreter = (taskId) => {
    setTasks({
      ...tasks,
      [taskId]: {
        ...tasks[taskId],
        interpreter: !tasks[taskId].interpreter,
      },
    });
  };

  const handleChangeNurseCheck = (taskId) => {
    setTasks({
      ...tasks,
      [taskId]: {
        ...tasks[taskId],
        is_nurse_check: !_.get(tasks[taskId], "is_nurse_check", false),
      },
    });
  };

  const handleChangeNewAppointment = (taskId) => {
    setTasks({
      ...tasks,
      [taskId]: {
        ...tasks[taskId],
        is_new_appointment: !_.get(tasks[taskId], "is_new_appointment", false),
      },
    });
  };

  const handleChangeTriage = (taskId) => {
    setTasks({
      ...tasks,
      [taskId]: {
        ...tasks[taskId],
        triage: !_.get(tasks[taskId], "triage", false),
      },
    });
  };

  return (
    <>
      <p className="menu-label">TASK ASSIGNMENT</p>

      <div className="columns is-variable">
        {taskList.map((task, index) => (
          <div key={index} className="column">
            <div className="mb-1">{task.name}</div>
            {task.id !== "gp_report_t" && (
              <div className="mb-1">
                <div className="control">
                  <CoordinatorSelect
                    isLoading={isCoordinatorsLoading}
                    coordinators={coordinators}
                    value={tasks[task.id].coordinator}
                    onChange={(e) => {
                      const newTask = produce(tasks, (draft) => {
                        draft[task.id].coordinator = e.target.value;
                      });
                      setTasks(newTask);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="mb-1">
              <div className="control">
                <div className="select is-size-7 is-fullwidth">
                  <select
                    className="is-radiusless"
                    disabled={task.id === "gp_report_t" && mustPickGpReport}
                    onChange={(e) => {
                      const newTask = produce(tasks, (draft) => {
                        draft[task.id].status = e.target.value;
                      });
                      setTasks(newTask);
                    }}
                    value={tasks[task.id].status}
                    title={task.id === "gp_report_t" && mustPickGpReport ? gpReportHint : undefined}
                  >
                    <option value="" disabled={true}>
                      Status
                    </option>
                    {statusesMapping[task.id].map((status, idx) => (
                      <option
                        key={idx}
                        value={status.value}
                        disabled={tasks[task.id].status === "" && task.id === "gp_report_t"}
                      >
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {task.id === "gp_report_t" && gpReportHint && (
                <p
                  className={`help is-size-7 ${outboundLetterCount > 0 ? "has-text-info" : "has-text-grey"}`}
                  style={{ marginTop: 4 }}
                >
                  {outboundLetterCount > 0 ? (
                    <span className="icon-text">
                      <span className="icon is-small">
                        <i className="fas fa-info-circle" />
                      </span>
                      <span>{gpReportHint}</span>
                    </span>
                  ) : (
                    gpReportHint
                  )}
                </p>
              )}
            </div>
            {task.id !== "gp_report_t" && (
              <div className="mb-1">
                <div className="control">
                  <textarea
                    className="textarea is-radiusless is-size-7"
                    value={tasks[task.id].notes}
                    onChange={(e) => {
                      const newTask = produce(tasks, (draft) => {
                        draft[task.id].notes = e.target.value;
                      });
                      setTasks(newTask);
                    }}
                  ></textarea>
                </div>
              </div>
            )}
            {task.id === "appointment_t" && (
              <>
                <div className="mb-1">
                  <div className="control">
                    <div className="is-size-7 is-fullwidth">
                      <input
                        className="form-week-to-appointment-input"
                        value={tasks[task.id].weeks_to_appointment || "0"}
                        disabled
                      ></input>
                      <input
                        className="is-small form-week-to-appointment-calendar"
                        value={bookingDate}
                        type="date"
                        placeholder="Weeks to Appointment"
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const anchorDay = (createdDate ? moment.unix(createdDate) : moment()).startOf("day");
                          const selectedDay = _.isEmpty(rawValue)
                            ? null
                            : moment(rawValue, TIME_FORMAT.DATE_WITH_DASH).startOf("day");
                          // A past due date is valid: it means the referral is already overdue.
                          // Keep it and let weeks go negative so the row is flagged (SE-803).
                          const hasDate = Boolean(selectedDay && selectedDay.isValid());
                          // Floor rather than truncate: diff(..., "w") rounds toward zero, which would store "0"
                          // for a date 1–6 days overdue and skip the overdue highlight.
                          const weeks = hasDate ? String(Math.floor(selectedDay.diff(anchorDay, "days") / 7)) : "0";
                          const expectedAppointmentDate = hasDate ? rawValue : null;
                          const date = hasDate ? selectedDay.format(TIME_FORMAT.DATE_WITH_DASH) : "";
                          const newTask = produce(tasks, (draft) => {
                            draft[task.id].weeks_to_appointment = weeks;
                            draft[task.id].expected_appointment_date = expectedAppointmentDate;
                          });
                          setTasks(newTask);
                          setBookingDate(date);
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-1">
                  <div className="control">
                    <div className="select is-size-7 is-fullwidth">
                      <select
                        className="is-radiusless"
                        onChange={(e) => {
                          const newTask = produce(tasks, (draft) => {
                            draft[task.id].cont_or_tbc = e.target.value;
                          });
                          setTasks(newTask);
                        }}
                        value={tasks[task.id].cont_or_tbc || ""}
                      >
                        <option value="">NRR or TBC</option>
                        {["NRR X 1", "NRR X 2", "TBC"].map((status, idx) => {
                          const dataValue = moment().format("YYYY-MM-DD");
                          return (
                            <option key={idx} value={`${status} ${dataValue}`}>
                              {`${status} ${dataValue}`}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mb-1">
                  <CheckboxField
                    label="New appointment"
                    checked={_.get(tasks[task.id], "is_new_appointment", false)}
                    onChange={() => handleChangeNewAppointment(task.id)}
                  />
                </div>
                <div className="mb-1">
                  <CheckboxField
                    label="Triage"
                    checked={_.get(tasks[task.id], "triage", false)}
                    onChange={() => handleChangeTriage(task.id)}
                  />
                </div>
                <div className="mb-1">
                  <CheckboxField
                    label="Biologics"
                    checked={tasks[task.id].biologics}
                    onChange={() => handleChangeBiologics(task.id)}
                  />
                </div>
                <div className="mb-1">
                  <CheckboxField
                    label="Interpreter"
                    checked={tasks[task.id].interpreter}
                    onChange={() => handleChangeInterpreter(task.id)}
                  />
                </div>
              </>
            )}
            {task.id === "pre_consult_t" && (
              <>
                <div className="mb-1">
                  <div className="control">
                    <div className="is-size-7 is-fullwidth pre_consult_container">
                      <Select
                        classNamePrefix="pre_consult"
                        placeholder={"Results"}
                        closeMenuOnSelect={false}
                        menuPlacement="top"
                        components={animatedComponents}
                        onChange={(selectOptions) => {
                          selectOptions = selectOptions || [];
                          const newTasks = produce(tasks, (draft) => {
                            draft[task.id].inspections = selectOptions.map((option) => option.value);
                          });
                          setTasks(newTasks);
                        }}
                        isMulti
                        value={alreadySelectOptions}
                        options={_.sortBy(inspectionOptions, ["label"])}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {task.id !== "gp_report_t" && (
              <div className="mb-1">
                <CheckboxField
                  label="This task is urgent."
                  checked={tasks[task.id].urgent}
                  onChange={() => handleChangeUrgency(task.id)}
                />
              </div>
            )}
            {task.id === "gp_report_t" && (
              <>
                <div className="mb-1">Admin Check Notes</div>
                <div className="level-left">
                  <input
                    className={`input is-radiusless is-size-7`}
                    type="text"
                    placeholder="Admin Check Notes"
                    onChange={(e) => setUpdatedFinNo(e.target.value)}
                    value={updatedFinNo}
                  />
                </div>
                <div className="mb-1">
                  <CheckboxField
                    label="Admin Check"
                    checked={tasks[task.id].is_nurse_check}
                    onChange={() => handleChangeNurseCheck(task.id)}
                  />
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="columns">
        <div className="column is-two-thirds" />
        <div className="column is-one-third">
          <Button text="Update" onClick={handleSubmit} isLoading={isLoading} />
        </div>
      </div>
    </>
  );
};

export default ManageTasksForm;
